// opd-nav.js — the SINGLE client-side-navigation choke point for the OPD SPA.
//
// Every internal navigation (programmatic `location.href =` assignments and
// internal <a> anchors) routes through navTo() / linkProps() here. react-router
// stays imported ONLY in App.jsx (which registers its navigate fn) and here —
// screens never import the router.
//
// SPA nav is DEFAULT-ON everywhere (owner 2026-08-11: web builds were shipping
// full-reload nav because the old opt-in env flag was never set in the build
// script — every sidebar click tore down the React tree and re-booted). The
// deployed demo/prod bundles had long proven SPA nav live (they were built
// manually with VITE_OPD_SPA_NAV=1), so the flag's caution had already been
// paid off; the default now matches the proven configuration instead of
// depending on a hand-typed env var at build time.
//
// `localStorage['opd.spaNav']='0'` remains the emergency runtime kill-switch on
// every platform — set it in the console + hard reload, no redeploy. The env
// var VITE_OPD_SPA_NAV=0 can also bake full-reload nav into a build.

// Read once at module load. `navTo`/`linkProps` are pure passthroughs while off.
const SPA_NAV_ON = (() => {
  try {
    const local = window.localStorage.getItem('opd.spaNav');
    if (local === '0') return false;
    if (local === '1') return true;
    return import.meta.env?.VITE_OPD_SPA_NAV !== '0';
  } catch { return false; }
})();

// The staff route set (design §4). SPA nav applies ONLY to these — anon/kiosk/
// magic-link surfaces stay full-reload on their Express bootstrap, and navTo/
// linkProps fall through to location.href for any non-staff path. Prefix match
// on the pathname only (query/hash stripped first).
const STAFF_PREFIXES = ['/queue', '/reception', '/nurse', '/emergency', '/ward',
  '/patients', '/reports', '/schedule', '/settings', '/lab', '/tasks', '/messaging',
  '/spaces', '/studio', '/automations', '/admin/', '/consult/', '/kiosk-setup'];

/** True when `href`'s pathname is one of the converted staff routes. */
export function isStaffPath(href) {
  const path = String(href || '').split(/[?#]/)[0];
  return STAFF_PREFIXES.some((p) => path === p.replace(/\/$/, '') || path.startsWith(p));
}

let _navigate = null;
/** App.jsx's <NavBridge/> registers react-router's navigate here at mount. */
export function registerNavigate(fn) { _navigate = fn; }

// ── Route chunk prefetch ───────────────────────────────────────────────────
// Each staff screen is a lazy() chunk (App.jsx SCREENS[].load). Under SPA nav a
// click that lands on an un-cached chunk shows a full-screen boot splash while
// the chunk downloads — the user gets no per-pill feedback and often re-taps
// ("takes a couple of clicks to get in"). Warming the chunk on pointer-ENTER /
// pointer-DOWN (before the click completes) means the module is already resolved
// by the time ScreenBoot mounts, so the destination appears near-instantly.
// App.jsx registers { pathname: load } here; AppShell calls prefetchRoute() from
// the nav links' pointer handlers. Each load() fires at most once (deduped).
let _prefetch = null;      // { '/schedule': () => import(...), ... }
const _warmed = new Set(); // pathnames already kicked off
/** App.jsx registers the path→load map (built from its SCREENS table). */
export function registerPrefetch(map) { _prefetch = map || null; }
/**
 * Warm the lazy chunk for `href`'s staff route. No-op when SPA nav is off (a
 * full reload discards the warmed module anyway), for non-staff paths, or once
 * already warmed. Best-effort — a failed prefetch is swallowed; the real load in
 * ScreenBoot surfaces any genuine error.
 */
export function prefetchRoute(href) {
  if (!SPA_NAV_ON || !_prefetch || !isStaffPath(href)) return;
  const path = String(href || '').split(/[?#]/)[0];
  if (_warmed.has(path)) return;
  const load = _prefetch[path];
  if (typeof load !== 'function') return;
  _warmed.add(path);
  try { Promise.resolve(load()).catch(() => {}); } catch { /* ignore */ }
}

// Warm EVERY screen's code chunk in the background after first paint (owner
// 2026-08-05: "a lot of loading pages — not a snappy experience", reviewing on
// an iPad where the hover-prefetch above never fires because there is no
// hover). One chunk per idle slice so boot-critical work is never contended;
// chunks are hash-cached immutably, so this costs each chunk's download once
// per deploy. Data freshness is untouched — this warms CODE only.
let _warmAllStarted = false;
export function prefetchAllRoutesWhenIdle() {
  if (_warmAllStarted || !SPA_NAV_ON || !_prefetch) return;
  _warmAllStarted = true;
  const paths = Object.keys(_prefetch);
  const idle = (fn) => (typeof requestIdleCallback === 'function'
    ? requestIdleCallback(fn, { timeout: 3000 })
    : setTimeout(fn, 350));
  const next = () => {
    const path = paths.find((p) => !_warmed.has(p));
    if (!path) return;
    prefetchRoute(path);
    idle(next);
  };
  idle(next);
}

/**
 * Replaces `window.location.href = url` for internal navigation.
 * Flag on + a registered navigate + a staff path → SPA navigate (no reload).
 * Otherwise → today's full-page load (location.href / location.replace).
 */
export function navTo(href, { replace = false } = {}) {
  if (SPA_NAV_ON && _navigate && isStaffPath(href)) { _navigate(href, { replace }); return; }
  if (replace) window.location.replace(href); else window.location.href = href;
}

/**
 * Spread onto an internal <a>: keeps the real href (so middle-click / cmd-click /
 * a11y / "copy link" all still work), and intercepts ONLY plain left-clicks.
 * With the flag off (or a non-staff href) it returns { href } only — a normal
 * anchor, i.e. today's full-reload behaviour.
 */
export function linkProps(href) {
  if (!SPA_NAV_ON || !isStaffPath(href)) return { href };
  return {
    href,
    onClick: (e) => {
      // Let the browser handle modified clicks (new tab/window), non-left
      // buttons, and anything a nearer handler already claimed.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      navTo(href);
    },
  };
}
